/**
 * Per-route document metadata for the product app.
 *
 * The app is a client-rendered CRA SPA served from one index.html, so until this
 * module existed every route shipped the same <title> and description. That hurt
 * in three separate ways, only one of which is SEO:
 *
 *   1. Tabs, history entries and bookmarks were indistinguishable. A recruiter
 *      with the wallet, a session and the admin console open saw three tabs all
 *      reading "TalentFora: AI Resume Screening".
 *   2. Screen readers announce document.title on navigation. A single static
 *      title means a route change is announced as no change at all.
 *   3. The handful of genuinely public routes (pricing, about, the legal set)
 *      were competing with their talentfora.in twins for the same search terms.
 *
 * ── How the entries are shaped ───────────────────────────────────────────────
 *
 * PAGE_META is keyed by the route path exactly as declared in App.js, so the two
 * files read side by side. Each entry carries:
 *
 *   title        the document title, before TITLE_SUFFIX is appended
 *   heading      optional; the in-app header label when it differs from `title`.
 *                RootLayout renders this, which is why the shell's page label and
 *                the tab title can no longer drift apart — they share one map.
 *   description  meta description, written from what the page actually does
 *   indexable    governs whether OG/Twitter tags are written. It no longer
 *                decides indexing -- see below; nothing on this host is indexed.
 *
 * ── Why most pages are noindex ───────────────────────────────────────────────
 *
 * Everything behind ProtectedRoute is unreachable to a crawler and personal to
 * one account, so it is marked noindex rather than left to chance. Token-bearing
 * routes (/reset-password/:token, /verify-email/:token, /invite/:token) are
 * noindex for a stronger reason: the token is in the URL, and an indexed URL is
 * a leaked credential.
 *
 * ── Why the off-domain canonical was removed (2026-09-11) ────────────────────
 *
 * /pricing, /about and the legal pages exist on BOTH app.talentfora.in and the
 * marketing site with substantially the same copy, and each app twin used to
 * point its canonical at the marketing counterpart to consolidate the two.
 *
 * That never worked. Everything in this module is written by JavaScript, so the
 * canonical was invisible to every crawler that does not execute JS -- which is
 * most of them, and all of the ones that generated the 2026-09 site audit. That
 * audit saw this host's /, /register and /loginNew as three pages sharing one
 * title, one description, no h1 and five words of body text.
 *
 * The fix is a header, not a tag: Front Door now stamps `X-Robots-Tag: noindex`
 * on every response from this host (rule set `appNoIndex` on `frontend-route`),
 * which reaches crawlers regardless of JavaScript. The cross-domain canonical
 * had to go with it -- the combination this file previously warned about, a
 * canonical pointing off-domain on a page that is also noindex, is exactly the
 * pairing that risks the noindex being applied to the marketing page we want
 * ranked. Public pages now self-canonicalise, which is inert under noindex.
 *
 * Not done with a 301 to the marketing twin, deliberately: app /pricing is a
 * working page, not a duplicate. The top-up and subscribe flows route through it
 * (CreditWalletPage, SubscribeCheckoutPage, CheckoutPage, the credit-balance
 * widget), so redirecting it at the edge would throw a signed-in user out to the
 * marketing site on any hard refresh or deep link.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Every title is rendered as `${title}${TITLE_SUFFIX}`. Matches the format the
 *  legal pages already used before this module absorbed them. */
const TITLE_SUFFIX = ' · TalentFora';

const APP_URL = (process.env.REACT_APP_APP_URL || 'https://app.talentfora.in').replace(/\/+$/, '');

const OG_IMAGE = `${APP_URL}/og-image.png`;

export const PAGE_META = {
  /* ── Public: duplicated on the marketing site, canonicalised onto it ─────── */

  '/pricing': {
    title: 'Pricing & Plans',
    description:
      'Compare TalentFora subscription plans and their monthly credit allowances. Monthly or annual billing, cards and UPI via Razorpay, and 50 free trial credits on every new account.',
    indexable: true,
  },
  '/about': {
    title: 'About TalentFora',
    description:
      'TalentFora is built by Forasoftware, a Microsoft Solutions Partner with offices in Dublin, Delhi, London, Istanbul and Sydney. Meet the product and the company behind it.',
    indexable: true,
  },
  '/terms': {
    title: 'Terms & Conditions',
    description:
      'The terms governing your use of TalentFora.in, the software-as-a-service platform operated by Softfoora Services Private Limited.',
    indexable: true,
  },
  '/privacy': {
    title: 'Privacy Policy',
    description:
      'How TalentFora collects, processes, stores and deletes personal data in candidate resumes, including your rights under the DPDP Act and GDPR and the sub-processors we rely on.',
    indexable: true,
  },
  '/cookies': {
    title: 'Cookie Policy',
    description:
      'The cookies and similar technologies TalentFora sets, what each category is used for, and how to change your consent at any time from the footer.',
    indexable: true,
  },
  '/cancellation-refund': {
    title: 'Cancellation & Refund Policy',
    description:
      'How to cancel a TalentFora subscription, what happens to unused credits, and the circumstances in which a refund is issued.',
    indexable: true,
  },
  '/shipping': {
    title: 'Shipping & Delivery Policy',
    description:
      'TalentFora is a digital service and nothing physical ships. This policy describes how access is delivered, as required of merchants by Indian payment-aggregator rules.',
    indexable: true,
  },
  '/sub-processors': {
    title: 'Sub-Processors',
    description:
      'The third-party sub-processors TalentFora uses, what each one processes, where it is hosted, and the safeguards applied to cross-border data transfers.',
    indexable: true,
  },
  '/ai-oversight': {
    title: 'AI & Human Oversight Notice',
    description:
      'How TalentFora uses AI to assist resume screening, why a ranking is never the final hiring decision, and the human oversight, logging and monitoring around it.',
    indexable: true,
  },
  '/contact': {
    title: 'Contact Us',
    description:
      'Reach TalentFora product support, Forasoftware sales, or the Grievance Officer, with office addresses and published response-time commitments.',
    indexable: true,
  },

  /* ── Public: app-only, self-canonical ───────────────────────────────────── */

  '/credit-packages': {
    title: 'Credit Top-Up Packages',
    description:
      'One-time credit packs for TalentFora resume ranking. Top-up credits never expire and stack on top of whatever your subscription grants each cycle.',
    indexable: true,
  },

  /* ── Authentication ─────────────────────────────────────────────────────── */

  '/loginNew': {
    title: 'Sign In',
    description:
      'Sign in to TalentFora with your email and password, a Microsoft work account, or Google to screen and rank resumes.',
    indexable: true,
  },
  '/register': {
    title: 'Create Your Account',
    description:
      'Create a TalentFora account and start with 5 trial credits, no card required. Sign up with email, a Microsoft work account, or Google.',
    indexable: true,
  },
  '/forgot-password': {
    title: 'Reset Your Password',
    description:
      'Request a password reset link for your TalentFora account.',
    indexable: true,
  },
  // Token in the URL — indexing this would publish a working credential.
  '/reset-password/:token': {
    title: 'Choose a New Password',
    description: 'Set a new password for your TalentFora account.',
  },
  '/verify-email/:token': {
    title: 'Verify Your Email',
    description: 'Confirm your email address to finish activating your TalentFora account.',
  },
  '/invite/:token': {
    title: 'Accept Your Invitation',
    description: 'Join your organisation on TalentFora and set up your account.',
  },

  /* ── The signed-in app ──────────────────────────────────────────────────── */

  '/dashboard': {
    title: 'Overview',
    description:
      'Your recruitment overview: resumes screened, ranking activity over time, candidate match-score distribution, and your most recent screening sessions.',
  },
  '/upload': {
    title: 'Upload & Rank Resumes',
    heading: 'Upload Records',
    description:
      'Paste or upload a job description, add resumes in bulk, and run AI ranking to score every candidate against the role.',
  },
  '/sessions': {
    title: 'Screening Sessions',
    heading: 'Sessions',
    description:
      'Browse, search and manage every resume-screening session on your account, and choose which one is active.',
  },
  '/sessions/:sessionId': {
    title: 'Session Results',
    description:
      'Ranked candidates for this screening session, with match scores, per-candidate score breakdowns, and report and resume downloads.',
  },
  '/library': {
    title: 'Resume Library',
    description:
      'Every resume stored on your account in one place: search it, download single files or a zip, restore from trash, or purge permanently.',
  },
  '/mailbox': {
    title: 'Connected Mailboxes',
    description:
      'Point TalentFora at a shared hiring inbox so resumes emailed there are imported automatically, instead of being saved and re-uploaded by hand.',
  },
  '/wallet': {
    title: 'Credit Wallet',
    description:
      'Your credit balance, 30-day usage analytics, transaction history, low-balance threshold alerts, and promo-code redemption.',
  },
  '/billing': {
    title: 'Billing History',
    heading: 'Billing Records',
    description:
      'Your TalentFora payment history: review past transactions, download individual invoices, and export the full billing ledger.',
  },
  '/profile': {
    title: 'Your Profile',
    heading: 'Profile',
    description:
      'View and update your TalentFora profile details, avatar and account information.',
  },
  '/settings': {
    title: 'Account Settings',
    heading: 'Settings',
    description:
      'Manage account preferences, change your password, review your subscription, and request account deletion.',
  },
  '/checkout/:packageId': {
    title: 'Checkout: Credit Package',
    description:
      'Review and complete your one-time TalentFora credit top-up, paid securely by card or UPI.',
  },
  '/subscribe/:planId': {
    title: 'Checkout: Subscription Plan',
    description:
      'Review and start your TalentFora subscription, with recurring billing handled securely by Razorpay.',
  },
  '/subscription': {
    title: 'Manage Subscription',
    description:
      'Check your plan status, next billing date and credits this cycle, switch plans, or cancel at the end of the current period.',
  },
  '/admin': {
    title: 'Organisation Administration',
    heading: 'Administration',
    description:
      'Admin console for your organisation: users and roles, invitations, credit requests, promo codes, audit logs and credit-usage reports.',
  },
  '/global-admin': {
    title: 'Global Administration',
    description:
      'Platform-wide console: organisations and their admins, deleted-account recovery, audit logs, credit reports, promo codes, analytics and gift credits.',
  },

  /* ── Errors ─────────────────────────────────────────────────────────────── */

  '/forbidden': {
    title: 'Access Denied',
    description: "You don't have permission to view this page.",
  },
  '*': {
    title: 'Page Not Found',
    description: 'The page you were looking for does not exist or has moved.',
  },
};

/* ── Route resolution ─────────────────────────────────────────────────────── */

// Patterns are pre-compiled once. Exact keys are matched first and never reach
// the regex list, so the common case is a plain object lookup.
const PATTERN_ROUTES = Object.keys(PAGE_META)
  .filter((path) => path.includes(':'))
  .map((path) => ({
    path,
    // A ":param" segment matches exactly one non-empty, non-slash segment. Every
    // other segment is escaped and matched literally.
    regexp: new RegExp(
      `^${path
        .split('/')
        .map((seg) =>
          seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        )
        .join('/')}/?$`
    ),
  }));

/**
 * The metadata for a pathname, or the catch-all entry when nothing matches —
 * which is correct, because an unmatched pathname is exactly what App.js renders
 * NotFound for.
 */
export function resolvePageMeta(pathname) {
  const exact = PAGE_META[pathname];
  if (exact) return exact;

  const matched = PATTERN_ROUTES.find(({ regexp }) => regexp.test(pathname));
  if (matched) return PAGE_META[matched.path];

  return PAGE_META['*'];
}

/**
 * The label the app shell shows for a route. Falls back to `title` so an entry
 * only needs `heading` when the two genuinely differ (e.g. the tab says
 * "Upload & Rank Resumes" while the header keeps the terser "Upload Records").
 */
export function pageHeading(pathname) {
  const meta = resolvePageMeta(pathname);
  return meta.heading || meta.title;
}

/* ── Applying metadata to <head> ──────────────────────────────────────────── */

// Marks the tags this module created, so cleanup removes them rather than
// removing something index.html shipped.
const MANAGED_ATTR = 'data-tf-page-meta';

/**
 * Point a head tag at `value`, creating it if index.html didn't ship one.
 * Returns a function that puts things back exactly as they were, so leaving a
 * route can't leak its metadata onto the next one.
 */
function setHeadTag(selector, createTag, valueAttr, value) {
  if (value == null) return null;

  let el = document.head.querySelector(selector);

  if (!el) {
    el = createTag();
    el.setAttribute(MANAGED_ATTR, '');
    el.setAttribute(valueAttr, value);
    document.head.appendChild(el);
    return () => el.remove();
  }

  const previous = el.getAttribute(valueAttr);
  el.setAttribute(valueAttr, value);
  return () => {
    if (previous === null) el.removeAttribute(valueAttr);
    else el.setAttribute(valueAttr, previous);
  };
}

const namedMeta = (name) => () => {
  const el = document.createElement('meta');
  el.setAttribute('name', name);
  return el;
};

const propertyMeta = (property) => () => {
  const el = document.createElement('meta');
  el.setAttribute('property', property);
  return el;
};

const linkRel = (rel) => () => {
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  return el;
};

/**
 * Write one page's metadata into <head>. Returns a single undo function.
 *
 * Open Graph and Twitter tags are written only for indexable pages. On a gated
 * route they'd be dead weight: no scraper can reach the page to read them, and
 * a JS-injected tag is invisible to most of them even when it can.
 */
function applyPageMeta(meta, pathname) {
  const undo = [];
  const push = (fn) => { if (fn) undo.push(fn); };

  const title = `${meta.title}${TITLE_SUFFIX}`;
  const previousTitle = document.title;
  document.title = title;
  undo.push(() => { document.title = previousTitle; });

  push(setHeadTag('meta[name="description"]', namedMeta('description'), 'content', meta.description));

  push(setHeadTag(
    'meta[name="robots"]',
    namedMeta('robots'),
    'content',
    meta.indexable ? 'index, follow' : 'noindex, nofollow'
  ));

  if (meta.indexable) {
    // Always self-canonical. These pages used to point their canonical at the
    // marketing twin; see the header comment for why that was removed.
    const canonical = `${APP_URL}${pathname}`;

    push(setHeadTag('link[rel="canonical"]', linkRel('canonical'), 'href', canonical));
    push(setHeadTag('meta[property="og:url"]', propertyMeta('og:url'), 'content', canonical));
    push(setHeadTag('meta[property="og:title"]', propertyMeta('og:title'), 'content', title));
    push(setHeadTag('meta[property="og:description"]', propertyMeta('og:description'), 'content', meta.description));
    push(setHeadTag('meta[property="og:image"]', propertyMeta('og:image'), 'content', OG_IMAGE));
    push(setHeadTag('meta[name="twitter:title"]', namedMeta('twitter:title'), 'content', title));
    push(setHeadTag('meta[name="twitter:description"]', namedMeta('twitter:description'), 'content', meta.description));
    push(setHeadTag('meta[name="twitter:image"]', namedMeta('twitter:image'), 'content', OG_IMAGE));
  } else {
    // A stale canonical is worse than none: it would tell a crawler that a gated
    // page is the same as whichever public page was open before it.
    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      const parent = existingCanonical.parentNode;
      const nextSibling = existingCanonical.nextSibling;
      existingCanonical.remove();
      undo.push(() => parent.insertBefore(existingCanonical, nextSibling));
    }
  }

  // Undo in reverse, so a tag this call created is removed before anything that
  // ran ahead of it tries to restore an attribute on it.
  return () => { for (let i = undo.length - 1; i >= 0; i -= 1) undo[i](); };
}

/**
 * Set the document title and meta tags for the current route.
 *
 * Call it with no arguments to take everything from PAGE_META:
 *
 *     usePageMeta();
 *
 * Or pass overrides to fold live data into the title — worth doing on the
 * dynamic routes, where the whole point is telling two open tabs apart:
 *
 *     usePageMeta({ title: session && `${session.name} · Session Results` });
 *
 * A null/undefined override field falls back to the map, so the call is safe to
 * make on the first render, before the data has loaded.
 */
export function usePageMeta(overrides) {
  const { pathname } = useLocation();

  const base = resolvePageMeta(pathname);
  const title = overrides?.title || base.title;
  const description = overrides?.description || base.description;

  if (process.env.NODE_ENV === 'development' && !PAGE_META[pathname] && base === PAGE_META['*']) {
    // Not an error for a genuinely unknown URL, but it is how a newly added route
    // gets noticed before it ships with the catch-all's title.
    // eslint-disable-next-line no-console
    console.warn(`[pageMeta] No entry for "${pathname}" — falling back to the 404 metadata.`);
  }

  useEffect(
    () => applyPageMeta({ ...base, title, description }, pathname),
    [base, title, description, pathname]
  );
}
